import {
    type ContractVariant,
    widgetPunctualityKpidirectionContract,
} from 'aos-ds/src/patterns/widget-punctuality-kpidirection/widget-punctuality-kpidirection.contract'
import { SeverityItem } from 'aos-ds/src/primitives/severityitem/SeverityItem'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { withoutSampleWidth } from '../../tailwind/withoutSampleWidth'
import {
    type PunctualityDirectionKey,
    PunctualityDirectionLabel,
} from '../punctuality-direction-label'
import {
    PunctualitySeverityBar,
    type PunctualitySeverityBarStatus,
    type PunctualitySeverityBarThresholds,
} from '../punctuality-severity-bar'
import type { TrendDirection } from '../trend-measure'
import { TrendMeasure } from '../trend-measure'
import { PredictionNotAvailable } from './PredictionNotAvailable'

export const PUNCTUALITY_KPI_DIRECTIONS =
    widgetPunctualityKpidirectionContract.props.direction.values
export const PUNCTUALITY_KPI_DIRECTION_LAYOUTS =
    widgetPunctualityKpidirectionContract.props.layout.values

export type PunctualityKpiDirectionLayout = (typeof PUNCTUALITY_KPI_DIRECTION_LAYOUTS)[number]

export type PunctualityKpiStatus = 'success' | 'warning' | 'critical' | 'neutral'

export type PunctualityKpiMeasure = {
    value?: number
    unit?: string
    trend: TrendDirection
    label: string
}

export type PunctualityKpiWindow = PunctualityKpiMeasure & { status: PunctualityKpiStatus }

export type PunctualityKpiTodayBar = {
    value: number | null
    status: PunctualitySeverityBarStatus
    thresholds: PunctualitySeverityBarThresholds | null
}

export type PunctualityKpiDirectionData = {
    label: string
    headline: PunctualityKpiMeasure
    last60: PunctualityKpiWindow
    next3h: PunctualityKpiWindow
    noPredictionReason?: string
    emptyValueText?: string
    bar?: PunctualityKpiTodayBar
}

export type PunctualityKpiDirectionProps = PunctualityKpiDirectionData & {
    direction: PunctualityDirectionKey
    layout?: PunctualityKpiDirectionLayout
    className?: string
    testID?: string
}

export const punctualityKpiDirectionKey = (
    direction: PunctualityDirectionKey,
    layout: PunctualityKpiDirectionLayout,
): string => `direction=${direction}, layout=${layout}`

export const resolvePunctualityKpiDirection = (
    direction: PunctualityDirectionKey,
    layout: PunctualityKpiDirectionLayout,
): ContractVariant => {
    const key = punctualityKpiDirectionKey(direction, layout)
    const cell = (
        widgetPunctualityKpidirectionContract.variants as Record<string, ContractVariant>
    )[key]
    if (!cell) {
        throw new Error(`PunctualityKpiDirection: the Figma contract has no "${key}"`)
    }
    return cell
}

export const kpiDirectionPart = (cell: ContractVariant, name: string): string => {
    const found = cell.parts[name]
    if (found === undefined) {
        throw new Error(`PunctualityKpiDirection: the Figma contract has no part "${name}"`)
    }
    return found
}

export { withoutSampleWidth }

const NO_VALUE = '–'

export type PunctualityKpiWindowRowProps = {
    kpi: PunctualityKpiWindow
    emptyValueText?: string
    reason?: string
    rowClassName?: string
    measureClassName?: string
    testID?: string
}

const stackedCell = () => resolvePunctualityKpiDirection('departures', 'stacked')

/**
 * One `Last 60 min` / `Next 3h` row of `Widget/Punctuality/KpiDirection`: the
 * small severity dot, the inline measure and, when the prediction is missing,
 * its reason icon. Spacing defaults to the stacked contract's row.
 */
export const PunctualityKpiWindowRow = ({
    kpi,
    emptyValueText = NO_VALUE,
    reason,
    rowClassName = kpiDirectionPart(stackedCell(), 'Last 60 min'),
    measureClassName = kpiDirectionPart(stackedCell(), 'Widget/Stat/TrendMeasure 2'),
    testID = 'punctuality-kpi-window-row',
}: PunctualityKpiWindowRowProps) => (
    <View className={twMerge('min-w-0 flex-row', rowClassName)} testID={testID}>
        <SeverityItem variant={kpi.status} size='small' className='shrink-0' />
        <TrendMeasure
            size='inline'
            value={kpi.value}
            unit={kpi.unit}
            label={kpi.label}
            emptyValueText={emptyValueText}
            trend={kpi.trend}
            className={twMerge('shrink', measureClassName)}
        />
        {reason !== undefined && <PredictionNotAvailable reason={reason} />}
    </View>
)

PunctualityKpiWindowRow.displayName = 'PunctualityKpiWindowRow'

/**
 * Figma `Widget/Punctuality/KpiDirection` (18906:121122): one direction of the
 * Punctuality KPI section — its label, the headline value with the bar slot
 * under it, and the Last 60 min / Next 3h rows. Every distance comes from the
 * contract. The frame width is the sample Figma drew; instances fill their
 * column, so it is dropped.
 *
 * `layout=wide` sets the values in a row beside the direction's delayed counts.
 * Those are `Widget/Stat/CountLabel`, which has no contract and no data yet, so
 * only the values are drawn.
 */
export const PunctualityKpiDirection = ({
    direction,
    layout = 'stacked',
    label,
    headline,
    last60,
    next3h,
    noPredictionReason,
    emptyValueText = NO_VALUE,
    bar,
    className,
    testID = 'punctuality-kpi-direction',
}: PunctualityKpiDirectionProps) => {
    const cell = resolvePunctualityKpiDirection(direction, layout)
    const container = withoutSampleWidth(cell.container)

    const values = (
        <>
            <View className={twMerge('w-full', kpiDirectionPart(cell, 'today'))}>
                <PunctualityDirectionLabel
                    direction={direction}
                    label={label}
                    className={kpiDirectionPart(cell, 'Widget/Punctuality/DirectionLabel')}
                />
                <TrendMeasure
                    size='small'
                    labelPosition='inline'
                    value={headline.value}
                    unit={headline.unit}
                    label={headline.label}
                    emptyValueText={emptyValueText}
                    trend={headline.trend}
                    className={twMerge(
                        'w-full',
                        kpiDirectionPart(cell, 'Widget/Stat/TrendMeasure'),
                    )}
                />
                {bar && (
                    <PunctualitySeverityBar
                        {...bar}
                        testID={`punctuality-today-bar-${direction}`}
                    />
                )}
            </View>
            <View className={twMerge('w-full', kpiDirectionPart(cell, 'windows'))}>
                <PunctualityKpiWindowRow
                    kpi={last60}
                    rowClassName={kpiDirectionPart(cell, 'Last 60 min')}
                    measureClassName={kpiDirectionPart(cell, 'Widget/Stat/TrendMeasure 2')}
                    emptyValueText={emptyValueText}
                    testID={`${testID}-last60`}
                />
                <PunctualityKpiWindowRow
                    kpi={next3h}
                    rowClassName={kpiDirectionPart(cell, 'Next 3h')}
                    measureClassName={kpiDirectionPart(cell, 'Widget/Stat/TrendMeasure 3')}
                    emptyValueText={emptyValueText}
                    reason={noPredictionReason}
                    testID={`${testID}-next3h`}
                />
            </View>
        </>
    )

    if (layout === 'wide') {
        return (
            <View
                className={twMerge('min-w-0 flex-1 flex-row', container, className)}
                testID={testID}
            >
                <View className={twMerge('min-w-0 flex-1', kpiDirectionPart(cell, 'values'))}>
                    {values}
                </View>
            </View>
        )
    }

    return (
        <View className={twMerge('min-w-0 flex-1', container, className)} testID={testID}>
            {values}
        </View>
    )
}

PunctualityKpiDirection.displayName = 'PunctualityKpiDirection'
